import type { PayloadAction } from '@reduxjs/toolkit';
import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { getAdminCenterData } from '@routes/sidebar/___shared___/model/adminCenterData.slice.ts';
import type { CommonTablePaginatedResponse } from '@routes/sidebar/admin-center/___shared___/types.ts';
import type {
    CreateShoreUserRequest,
    CsvUploadStateResponse,
    ShoreUser,
    ShoreUsersRequest,
    ShoreUsersResponse,
    UpdateShoreUserRequest,
} from '@routes/sidebar/admin-center/tenant-client/___pages___/configuration/___pages___/shore-users/api/shoreUsers.api.ts';
import {
    downloadApiShoreUsersCsv,
    downloadApiShoreUsersExampleCsv,
    getApiShoreUsersCsvUpload,
    uploadApiShoreUsersCsv,
} from '@routes/sidebar/admin-center/tenant-client/___pages___/configuration/___pages___/shore-users/api/shoreUsers.api.ts';
import {
    createApiShoreUser,
    deleteApiShoreUser,
    getApiShoreUsers,
    updateApiShoreUser,
} from '@routes/sidebar/admin-center/tenant-client/___pages___/configuration/___pages___/shore-users/api/shoreUsers.api.ts';
import { SHORE_USERS_STEP_TITLE } from '@routes/sidebar/admin-center/tenant-client/___pages___/configuration/___pages___/shore-users/constants.ts';
import type { ShoreUsersRow } from '@routes/sidebar/admin-center/tenant-client/___pages___/configuration/___pages___/shore-users/ui/table/types.ts';
import { CSV_UPLOAD_STATUS } from '@routes/sidebar/admin-center/tenant-client/___pages___/configuration/___shared___/constants.ts';
import { setPublishedSteps } from '@routes/sidebar/admin-center/tenant-client/___pages___/configuration/model/configuration.slice.ts';
import { downloadFromDisposition } from '@utils/download.ts';
import { extractError } from '@utils/slice.ts';

export interface ShoreUsersError {
    message?: string;
    [k: string]: any;
}

export interface ShoreUsersState {
    error: ShoreUsersError | null;
    isCsvImportErroneous: boolean;
    isCsvImportFailed: boolean;
    uploadedCsvFilename: string;
    isDeleteShoreUserModalOpened: boolean;
    isActivateShoreUserModalOpened: boolean;
    isDeactivateShoreUserModalOpened: boolean;
    isUploadCsvModalOpened: boolean;
    uploadedCsvFile: File | null;
    uploadedCsvId: string | null;
    csvUploadState: CsvUploadStateResponse | null;

    commonShoreUsers: ShoreUsersResponse | null;
    shoreUsers: ShoreUser[];
    pagination: CommonTablePaginatedResponse;

    updatedShoreUser: ShoreUser | null;
    selectedShoreUser: ShoreUsersRow | null;
    selectedRowInitialData: ShoreUsersRow | null;
    currentGetRequestId: string | null;

    isGetShoreUsersPending: boolean;
    isCreateShoreUserPending: boolean;
    isUpdateShoreUserPending: boolean;
    isDeleteShoreUserPending: boolean;
    isUploadCsvPending: boolean;
    isGetCsvUploadPending: boolean;
    isDownloadCsvPending: boolean;
    isGetExampleCsvPending: boolean;
}

const initialState: ShoreUsersState = {
    error: null,
    isCsvImportErroneous: false,
    isCsvImportFailed: false,
    uploadedCsvFilename: '',
    isDeleteShoreUserModalOpened: false,
    isActivateShoreUserModalOpened: false,
    isDeactivateShoreUserModalOpened: false,
    isUploadCsvModalOpened: false,
    uploadedCsvFile: null,
    uploadedCsvId: null,
    csvUploadState: null,

    commonShoreUsers: null,
    shoreUsers: [],
    pagination: {
        pageNumber: 0,
        pageSize: 0,
        totalPages: 0,
        totalResults: 0,
        maxPageSize: 0,
    },
    updatedShoreUser: null,
    selectedShoreUser: null,
    selectedRowInitialData: null,
    currentGetRequestId: null,

    isGetShoreUsersPending: false,
    isCreateShoreUserPending: false,
    isUpdateShoreUserPending: false,
    isDeleteShoreUserPending: false,
    isUploadCsvPending: false,
    isGetCsvUploadPending: false,
    isDownloadCsvPending: false,
    isGetExampleCsvPending: false,
};

export const getShoreUsers = createAsyncThunk<
    ShoreUsersResponse,
    { clientId: number; tenantAlias: string; payload: ShoreUsersRequest },
    { rejectValue: ShoreUsersError }
>('adminCenterShoreUsers/getShoreUsers', ({ clientId, tenantAlias, payload }, { rejectWithValue, dispatch }) =>
    getApiShoreUsers(clientId, tenantAlias, payload)
        .then((res) => {
            if (res.data?.length > 0) {
                dispatch(setPublishedSteps(SHORE_USERS_STEP_TITLE));
            }

            return res;
        })
        .catch((err) => rejectWithValue(err)),
);

export const createShoreUser = createAsyncThunk<
    ShoreUser,
    { clientId: number; tenantAlias: string; payload: CreateShoreUserRequest },
    { rejectValue: ShoreUsersError }
>('adminCenterShoreUsers/createShoreUser', ({ clientId, tenantAlias, payload }, { rejectWithValue }) =>
    createApiShoreUser(clientId, tenantAlias, payload)
        .then((res) => res)
        .catch((err) => rejectWithValue(err)),
);

export const updateShoreUser = createAsyncThunk<
    ShoreUser,
    { clientId: number; tenantAlias: string; id: number; payload: UpdateShoreUserRequest },
    { rejectValue: ShoreUsersError }
>('adminCenterShoreUsers/updateShoreUser', ({ clientId, tenantAlias, id, payload }, { rejectWithValue }) =>
    updateApiShoreUser(clientId, tenantAlias, id, payload)
        .then((res) => res)
        .catch((err) => rejectWithValue(err)),
);

export const deleteShoreUser = createAsyncThunk<
    ShoreUser,
    { clientId: number; tenantAlias: string; id: number },
    { rejectValue: ShoreUsersError }
>('adminCenterShoreUsers/deleteShoreUser', ({ clientId, tenantAlias, id }, { rejectWithValue }) =>
    deleteApiShoreUser(clientId, tenantAlias, id)
        .then((res) => res)
        .catch((err) => rejectWithValue(err)),
);

export const uploadShoreUsersCsv = createAsyncThunk<
    string,
    { clientId: number; tenantAlias: string; formData: FormData },
    { rejectValue: ShoreUsersError }
>('adminCenterShoreUsers/uploadShoreUsersCsv', ({ clientId, tenantAlias, formData }, { rejectWithValue }) =>
    uploadApiShoreUsersCsv(clientId, tenantAlias, formData)
        .then((res) => res)
        .catch((err) => rejectWithValue(err)),
);

export const getShoreUsersCsvUpload = createAsyncThunk<
    CsvUploadStateResponse,
    { clientId: number; tenantAlias: string; id: string },
    { rejectValue: ShoreUsersError }
>('adminCenterShoreUsers/getShoreUsersCsvUpload', ({ clientId, tenantAlias, id }, { rejectWithValue, dispatch }) =>
    getApiShoreUsersCsvUpload(clientId, tenantAlias, id)
        .then((res) => {
            if (res.uploadStatus === CSV_UPLOAD_STATUS.SUCCESS) {
                dispatch(setPublishedSteps(SHORE_USERS_STEP_TITLE));
            }
            return res;
        })
        .catch((err) => rejectWithValue(err)),
);

export const downloadShoreUsersCsv = createAsyncThunk<
    void,
    { clientId: number; tenantAlias: string },
    { rejectValue: ShoreUsersError }
>('adminCenterShoreUsers/downloadShoreUsersCsv', ({ clientId, tenantAlias }, { rejectWithValue }) =>
    downloadApiShoreUsersCsv(clientId, tenantAlias)
        .then((res) => {
            downloadFromDisposition(res);
        })
        .catch((err) => rejectWithValue(err)),
);

export const downloadShoreUsersExampleCsv = createAsyncThunk<
    void,
    { clientId: number; tenantAlias: string },
    { rejectValue: ShoreUsersError }
>('adminCenterShoreUsers/downloadShoreUsersExampleCsv', ({ clientId, tenantAlias }, { rejectWithValue }) =>
    downloadApiShoreUsersExampleCsv(clientId, tenantAlias)
        .then((res) => {
            downloadFromDisposition(res);
        })
        .catch((err) => rejectWithValue(err)),
);

const shoreUsersSlice = createSlice({
    name: 'adminCenterShoreUsers',
    initialState,
    reducers: {
        setIsCsvImportErroneous(state, action: PayloadAction<boolean>) {
            state.isCsvImportErroneous = action.payload;
        },
        setIsCsvImportFailed(state, action: PayloadAction<boolean>) {
            state.isCsvImportFailed = action.payload;
        },
        setUploadedCsvFilename(state, action: PayloadAction<string>) {
            state.uploadedCsvFilename = action.payload;
        },
        setIsDeleteShoreUserModalOpened(state, action: PayloadAction<boolean>) {
            state.isDeleteShoreUserModalOpened = action.payload;
        },
        setIsActivateShoreUserModalOpened(state, action: PayloadAction<boolean>) {
            state.isActivateShoreUserModalOpened = action.payload;
        },
        setIsDeactivateShoreUserModalOpened(state, action: PayloadAction<boolean>) {
            state.isDeactivateShoreUserModalOpened = action.payload;
        },
        setIsUploadCsvModalOpened(state, action: PayloadAction<boolean>) {
            state.isUploadCsvModalOpened = action.payload;
        },
        setUploadedCsvFile(state, action: PayloadAction<File | null>) {
            state.uploadedCsvFile = action.payload;
        },
        setSelectedShoreUser(state, action: PayloadAction<ShoreUsersRow | null>) {
            state.selectedShoreUser = action.payload;
        },
        setCsvUploadState(state, action: PayloadAction<CsvUploadStateResponse | null>) {
            state.csvUploadState = action.payload;
        },
        clearShoreUsersState(state) {
            state.isCsvImportErroneous = false;
            state.isCsvImportFailed = false;
            state.csvUploadState = null;
            state.uploadedCsvFilename = '';
            state.uploadedCsvFile = null;
            state.uploadedCsvId = null;
        },
        setSelectedRowInitialData(state, action: PayloadAction<ShoreUsersRow | null>) {
            state.selectedRowInitialData = action.payload;
        },
        setShoreUsers(state, action: PayloadAction<ShoreUser[]>) {
            state.shoreUsers = action.payload;
        },
    },
    extraReducers: (builder) => {
        builder.addCase(getShoreUsers.pending, (state, action) => {
            state.isGetShoreUsersPending = true;
            state.error = null;
            state.currentGetRequestId = action.meta.requestId;
        });
        builder.addCase(getShoreUsers.fulfilled, (state, action) => {
            if (state.currentGetRequestId !== action.meta.requestId) return;

            state.isGetShoreUsersPending = false;
            state.currentGetRequestId = null;
            const { data, pagination } = action.payload;
            state.commonShoreUsers = action.payload;
            state.shoreUsers = data ?? [];
            state.pagination = pagination;
        });
        builder.addCase(getShoreUsers.rejected, (state, action) => {
            if (state.currentGetRequestId !== action.meta.requestId) return;

            state.isGetShoreUsersPending = false;
            state.currentGetRequestId = null;
            state.error = extractError(action);
        });

        builder.addCase(createShoreUser.pending, (state) => {
            state.isCreateShoreUserPending = true;
            state.error = null;
        });
        builder.addCase(createShoreUser.fulfilled, (state, action) => {
            state.isCreateShoreUserPending = false;
            state.updatedShoreUser = action.payload;
        });
        builder.addCase(createShoreUser.rejected, (state, action) => {
            state.isCreateShoreUserPending = false;
            state.error = extractError(action);
        });

        builder.addCase(updateShoreUser.pending, (state) => {
            state.isUpdateShoreUserPending = true;
            state.error = null;
        });
        builder.addCase(updateShoreUser.fulfilled, (state, action) => {
            state.isUpdateShoreUserPending = false;
            state.updatedShoreUser = action.payload;
        });
        builder.addCase(updateShoreUser.rejected, (state, action) => {
            state.isUpdateShoreUserPending = false;
            state.error = extractError(action);
        });

        builder.addCase(deleteShoreUser.pending, (state) => {
            state.isDeleteShoreUserPending = true;
            state.error = null;
        });
        builder.addCase(deleteShoreUser.fulfilled, (state, action) => {
            const deletedId = action.payload.id;

            state.isDeleteShoreUserPending = false;
            state.updatedShoreUser = action.payload;
            state.shoreUsers = state.shoreUsers.filter((u) => u.id !== deletedId);
        });
        builder.addCase(deleteShoreUser.rejected, (state, action) => {
            state.isDeleteShoreUserPending = false;
            state.error = extractError(action);
        });
        builder.addCase(uploadShoreUsersCsv.pending, (state) => {
            state.isUploadCsvPending = true;
            state.error = null;
            state.isCsvImportFailed = false;
            state.uploadedCsvId = null;
            state.csvUploadState = null;
        });
        builder.addCase(uploadShoreUsersCsv.fulfilled, (state, action) => {
            state.isUploadCsvPending = false;
            state.uploadedCsvId = action.payload;
            state.uploadedCsvFile = null;
        });
        builder.addCase(uploadShoreUsersCsv.rejected, (state, action) => {
            state.isUploadCsvPending = false;
            state.isCsvImportFailed = true;
            state.error = extractError(action);
        });
        builder.addCase(getShoreUsersCsvUpload.pending, (state) => {
            state.isGetCsvUploadPending = true;
            state.error = null;
            state.isCsvImportErroneous = false;
        });

        builder.addCase(getShoreUsersCsvUpload.fulfilled, (state, action) => {
            state.isGetCsvUploadPending = false;
            state.csvUploadState = action.payload;
            const { uploadStatus } = action.payload;

            if (uploadStatus === CSV_UPLOAD_STATUS.SUCCESS) {
                state.uploadedCsvId = null;
            }
        });

        builder.addCase(getShoreUsersCsvUpload.rejected, (state, action) => {
            state.isGetCsvUploadPending = false;
            state.error = extractError(action);
        });

        builder.addCase(downloadShoreUsersCsv.pending, (state) => {
            state.isDownloadCsvPending = true;
            state.error = null;
        });
        builder.addCase(downloadShoreUsersCsv.fulfilled, (state) => {
            state.isDownloadCsvPending = false;
        });
        builder.addCase(downloadShoreUsersCsv.rejected, (state, action) => {
            state.isDownloadCsvPending = false;
            state.error = extractError(action);
        });

        builder.addCase(downloadShoreUsersExampleCsv.pending, (state) => {
            state.isGetExampleCsvPending = true;
            state.error = null;
        });
        builder.addCase(downloadShoreUsersExampleCsv.fulfilled, (state) => {
            state.isGetExampleCsvPending = false;
        });
        builder.addCase(downloadShoreUsersExampleCsv.rejected, (state, action) => {
            state.isGetExampleCsvPending = false;
            state.error = extractError(action);
        });

        builder.addCase(getAdminCenterData.fulfilled, (state, action) => {
            const { shoreUserResponse } = action.payload;

            if (!shoreUserResponse) return;

            const { data, pagination } = shoreUserResponse;
            state.commonShoreUsers = shoreUserResponse;
            state.shoreUsers = data ?? [];
            state.pagination = pagination;
        });
    },
});

export const {
    setIsCsvImportErroneous,
    setIsCsvImportFailed,
    setUploadedCsvFilename,
    setIsDeleteShoreUserModalOpened,
    setIsActivateShoreUserModalOpened,
    setIsDeactivateShoreUserModalOpened,
    setIsUploadCsvModalOpened,
    setUploadedCsvFile,
    setSelectedShoreUser,
    setCsvUploadState,
    clearShoreUsersState,
    setSelectedRowInitialData,
    setShoreUsers,
} = shoreUsersSlice.actions;

export default shoreUsersSlice.reducer;
